import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { Router } from "@angular/router";
import { ButtonComponent } from "@components/button/button.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { InputComponent } from "@components/input/input.component";
import { PageComponent } from "@components/page/page.component";
import { SearchComponent } from "@components/search/search.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { SubmitButtonComponent } from "@components/submit-button/submit-button.component";
import { TreeSelectorComponent } from "@components/tree-selector/tree-selector.component";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { take } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { SelectionItem } from "~app/common/components/tree-selector/selection-item";
import { ITypeAheadDTO } from "~app/common/models/common/ITypeAheadDTO";
import { KundorderTyp } from "~app/common/models/constants";
import { ITypeAheadResult } from "~app/common/models/type-ahead-result.model";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { EventService } from "~app/common/services/cqs/event.service";
import { StatedataService, Volatile } from "~app/common/services/statedata.service";
import { WebinstallningarService } from "~app/common/services/webinstallningar/webinstallningar.service";
import { DateUtils } from "~app/common/utils/date-utils";
import { HandelssortimentTreeType, KundorderListRequestDTO, NamnIdDTO } from "../models/KundorderListRequestDTO";
import { Kundorder } from "../models/KundorderListResponseDTO";
import { TerminalorderService } from "../terminalorder/terminalorder.service";
import { CreateKundorderModalComponent } from "./create-kundorder-modal/create-kundorder-modal.component";
import { KundorderCreatedMessage } from "./events/KundorderCreatedMessage";
import { KundorderListComponent } from "./kundorder-list/kundorder-list.component";
import { KundorderService } from "./kundorder.service";

@Component({
    selector: "rask-kundorder",
    templateUrl: "./kundorder.component.html",
    styleUrls: ["./kundorder.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PageComponent,
        FormsModule,
        ReactiveFormsModule,
        SearchComponent,
        InputComponent,
        DatepickerComponent,
        SelectorComponent,
        TreeSelectorComponent,
        SubmitButtonComponent,
        ButtonComponent,
        KundorderListComponent,
        TranslatePipe
    ]
})
export class KundorderComponent implements OnInit {
    avtalsansvarig: BehaviorSubject<ITypeAheadResult> = new BehaviorSubject<ITypeAheadResult>(null);
    form: UntypedFormGroup;
    handelssortiment: SelectionItem[] = [];
    kundordrar: Kundorder[];
    request: KundorderListRequestDTO;
    volatile: Volatile;

    private readonly WIK = "kundorder.search";
    private readonly authenticationService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly eventService = inject(EventService);
    private readonly formBuilder = inject(UntypedFormBuilder);
    private readonly kundorderService = inject(KundorderService);
    private readonly modalService = inject(RaskModalService);
    private readonly router = inject(Router);
    private readonly stateDataService = inject(StatedataService);
    private readonly terminalorderService = inject(TerminalorderService);
    private readonly translateService = inject(TranslateService);
    private readonly webinstallningarService = inject(WebinstallningarService);

    constructor() {
        this.volatile = this.stateDataService.getVolatile();
        this.form = this.emptyForm();
    }

    avtalAnsvarigSearch = (input: string) => this.kundorderService.searchForAvtalsansvarigTypeahead(input);

    avtalAnsvarigViewFormatter = (item: any) => item?.namn ?? item?.name;

    avtalTypeAheadValueFormatter = (item: ITypeAheadResult) => item;

    canEdit(): boolean {
        return this.authenticationService.canEdit();
    }

    create = (): void => {
        this.modalService.open(this.translateService.instant("KUNDORDER_LIST_SKAPA_NY"), CreateKundorderModalComponent, "xl", true, KundorderTyp.Kundorder);
    };

    kundTypeAhead = (input: string) => this.kundorderService.searchForKundTypeAhead(input);

    mottagninsplatsTypeAhead = (input: string) => this.kundorderService.searchForMottagningsplatsTypeahead(input);

    ngOnInit() {
        this.request = this.volatile.get<KundorderListRequestDTO>(this.WIK);
        if (this.request) {
            this.setupHandelssortimentTreeSelector(this.request?.handelssortiment).subscribe(() => {
                this.mapToForm(this.request);
                this.avtalsansvarig.next(this.request.avtalsansvarig);
                this.searchKundorder(false);
            });
            return;
        }

        this.eventService.subscribeEvent<KundorderCreatedMessage>(KundorderCreatedMessage.$type).subscribe(k => {
            this.router.navigate([
                "kundorder",
                k.kundorderId
            ]);
        });

        this.resetToWebinstallning();
    }

    reset() {
        this.request = new KundorderListRequestDTO();
        this.form.get("kund").setValue(new NamnIdDTO());
        this.form.get("beskrivning").setValue(null);
        this.form.get("periodFrom").setValue(null);
        this.form.get("periodTom").setValue(null);
        this.form.get("avtalsansvarig").setValue({ kod: null,
            namn: null });
        this.form.get("handelssortiment").setValue([]);
        this.form.get("mottagningsplats").setValue(new NamnIdDTO());
        this.form.get("ordernummer").setValue(null);
        this.setupHandelssortimentTreeSelector([]).subscribe();
    }

    resetToWebinstallning() {
        this.webinstallningarService.getAsync<KundorderListRequestDTO>(this.WIK)
            .then((values: KundorderListRequestDTO) => {
                this.setupHandelssortimentTreeSelector(values?.handelssortiment).subscribe(() => {
                    if (values) {
                        this.request = values;
                        this.mapToForm(values);
                    }
                });
            });
    }

    set = (val) => of([val]);

    searchKundorder = (saveState = true) => {
        this.busyService.addWork(this.kundorderService.search(this.mapFromForm()))
            .pipe(take(1))
            .subscribe(result => {
                this.kundordrar = result.kundorderList;
                if (saveState) {
                    this.volatile.set(this.WIK, this.mapFromForm());
                    this.webinstallningarService.set<KundorderListRequestDTO>(this.WIK, this.mapFromForm());
                }
            });
    };

    private emptyForm(): UntypedFormGroup {
        this.request = new KundorderListRequestDTO();
        this.request.kund = new NamnIdDTO();
        this.request.beskrivning = null;
        this.request.periodFrom = null;
        this.request.periodTom = null;
        this.request.avtalsansvarig = { kod: null,
            namn: null } as ITypeAheadDTO;
        this.request.handelssortiment = [];
        this.request.mottagningsplats = new NamnIdDTO();
        this.request.ordernummer = null;
        return this.formBuilder.group(this.request);
    }

    private mapFromForm(): KundorderListRequestDTO {
        const periodFrom = DateUtils.nullableDate(this.form.get("periodFrom").value);
        const periodTom = DateUtils.nullableDate(this.form.get("periodTom").value);

        return {
            kund: this.form.get("kund").value ?? new NamnIdDTO(),
            beskrivning: this.form.get("beskrivning").value ?? "",
            periodFrom: periodFrom,
            periodTom: periodTom,
            avtalsansvarig: this.form.get("avtalsansvarig").value ?? { kod: null,
                namn: null },
            handelssortiment: this.terminalorderService.getSelectedHandelssortiment(this.handelssortiment) ?? [],
            mottagningsplats: this.form.get("mottagningsplats").value ?? new NamnIdDTO(),
            ordernummer: this.form.get("ordernummer").value ?? ""
        } as KundorderListRequestDTO;
    }

    private mapToForm(values: KundorderListRequestDTO) {
        this.form = this.emptyForm();
        this.form.controls.kund.setValue(values.kund ?? new NamnIdDTO());
        this.form.controls.beskrivning.setValue(values.beskrivning ?? "");
        this.form.controls.periodFrom.setValue(DateUtils.nullableDate(values.periodFrom));
        this.form.controls.periodTom.setValue(DateUtils.nullableDate(values.periodTom));
        this.form.controls.avtalsansvarig.setValue(values.avtalsansvarig ?? { kod: null,
            namn: null });
        this.avtalsansvarig.next(this.form.controls.avtalsansvarig.value);
        this.form.controls.handelssortiment.setValue(this.terminalorderService.getSelectedHandelssortiment(this.handelssortiment) ?? []);
        this.form.controls.mottagningsplats.setValue(values.mottagningsplats);
        this.form.controls.ordernummer.setValue(values.ordernummer ?? "");
    }

    private readonly setupHandelssortimentTreeSelector = (valdaHs: HandelssortimentTreeType[]): Observable<any> => new Observable<any>(cb => {
        this.terminalorderService.getTerminalorderBasData()
            .subscribe(p => {
                this.handelssortiment = this.terminalorderService.setupHandelssortimentTreeSelector(p.handelssortiment, valdaHs);
                cb.next(null);
            });
    });
}
